/**
 * WP2.6 — deal coordinator helpers (moderator panel only).
 * Backend: `apps/coordinator` (`/api/v1/moderators/coordinator/...`).
 * Hard rule: drafts reach a company only after a moderator approves them.
 */
import i18n from "../i18n";
import { getApiErrorMessage, getApiErrorStatus } from "./apiError";

export type DraftStatus = "pending" | "approved" | "rejected";
export type ChipVariant = "warning" | "success" | "danger";

function normalizeStatus(status: string | null | undefined): DraftStatus {
  return status === "approved" || status === "rejected" ? status : "pending";
}

export function draftStatusLabel(status: string | null | undefined): string {
  return i18n.t(`deals:coordinator.draftStatus.${normalizeStatus(status)}`);
}

export function draftStatusVariant(status: string | null | undefined): ChipVariant {
  const s = normalizeStatus(status);
  return s === "approved" ? "success" : s === "rejected" ? "danger" : "warning";
}

export function recipientLabel(role: string | null | undefined): string {
  return i18n.t(`deals:coordinator.recipient.${role === "buyer" ? "buyer" : "supplier"}`);
}

export function channelLabel(channel: string | null | undefined): string {
  return i18n.t(`deals:coordinator.channel.${channel === "notification" ? "notification" : "message"}`);
}

export function sourceLabel(source: string | null | undefined): string {
  const s = source === "stall_rule" || source === "moderator" ? source : "agent";
  return i18n.t(`deals:coordinator.source.${s}`);
}

/** "1 h 25 min" style total for the time log. */
export function formatMinutes(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return i18n.t("deals:coordinator.minutes", { count: minutes });
  return i18n.t("deals:coordinator.hoursMinutes", { hours, minutes });
}

const COORDINATOR_PREFIX = "/api/v1/moderators/coordinator";

/** Query-key predicate for every coordinator query (queue, panel, flags). */
export function isCoordinatorQuery(queryKey: readonly unknown[]): boolean {
  const head = queryKey[0];
  return typeof head === "string" && head.startsWith(COORDINATOR_PREFIX);
}

/** Total `count` of a paginated `{count, results}` response (orval types these loosely). */
export function paginatedCount(data: unknown): number {
  if (data && typeof data === "object" && "count" in data) {
    const count = (data as { count?: unknown }).count;
    return typeof count === "number" ? count : 0;
  }
  return 0;
}

/** Message for a failed LLM action: "not configured" (503), model failure (502) or the API's own error. */
export function coordinatorErrorMessage(err: unknown, fallback: string): string {
  const status = getApiErrorStatus(err);
  if (status === 503) return i18n.t("deals:coordinator.notConfigured");
  if (status === 502) return i18n.t("deals:coordinator.insights.errors.llmFailed");
  return getApiErrorMessage(err, fallback);
}

export type StallRule = "pending_consent" | "no_messages" | "no_stage_change";

/** Human label for a stalled-deal flag, e.g. "No messages for 9 days". */
export function stallRuleLabel(rule: string | null | undefined, days: number): string {
  const r: StallRule = rule === "pending_consent" || rule === "no_messages" ? rule : "no_stage_change";
  return i18n.t(`deals:coordinator.stall.rules.${r}`, { count: days });
}
